import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import useOrderService from '../../hooks/useOrderService';
import useProviderService from '../../hooks/useProviderService';
import useBuildingService from '../../hooks/useBuildingService';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
    Button,
    TextField,
    Grid,
    Box,
    Paper,
    Typography,
    CircularProgress,
    Autocomplete,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper as TablePaper,
    IconButton
} from "@mui/material";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import DeleteIcon from '@mui/icons-material/Delete';
import { handleApiError, DatePickerComponent } from '../../components/Utils';

dayjs.extend(utc);

function NewOrder() {
    const { createOrder } = useOrderService();
    const { getProviders } = useProviderService();
    const { getBuildings } = useBuildingService();
    const showSnackbar = useSnackbar();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const [productForm, setProductForm] = useState([]);
    const [errorPriceMessage, setErrorPriceMessage] = useState(null);
    const [errorQuantityMessage, setErrorQuantityMessage] = useState(null);
    const [errorProductNameMessage, setErrorProductNameMessage] = useState(null);

    const initialNewProductState = {
        product_name: '',
        price: '',
        quantity: '',
        unit: '',
    };

    const [newProduct, setNewProduct] = useState(initialNewProductState);

    const [orderForm, setOrderForm] = useState({
        provider_id: null,
        building_id: null,
        provider_code: null,
        order_date: dayjs().utc().format('YYYY-MM-DD'),
    });

    const fetchProviders = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getProviders();
            setProviders(result.data.items);
            setLoading(false);
        } catch (error) {
            setError(handleApiError(error, 'Error al obtener proveedores.'));
        }
    }, []);

    const fetchBuildings = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getBuildings();
            setBuildings(result.data.items);
            setLoading(false);
        } catch (error) {
            setError(handleApiError(error, 'Error al obtener obras.'));
        }
    }, []);

    useEffect(() => {
        fetchProviders();
        fetchBuildings();
    }, []);

    const handleDateChange = (newValue) => {
        let offerDate = dayjs(newValue).hour(12).minute(0).second(0);
        offerDate = offerDate.utc().format('YYYY-MM-DD');
        setOrderForm(prevState => ({
            ...prevState,
            order_date: offerDate
        }));
    };

    const handleProviderChange = (event, newValue) => {
        setSelectedProvider(newValue);
        setOrderForm(prevState => ({
            ...prevState,
            provider_id: newValue ? newValue.id : null
        }));
    };
    const handleBuildingChange = (event, newValue) => {
        setSelectedBuilding(newValue);
        setOrderForm(prevState => ({
            ...prevState,
            building_id: newValue ? newValue.id : null
        }));
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setOrderForm(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const validateForm = () => {
        if (!orderForm.provider_id) {
            return "Por favor, añada un proveedor";
        }
        if (!orderForm.order_date) {
            return "Por favor, añada una fecha";
        }
        if (orderForm.provider_code && orderForm.provider_code.length > 36) {
            return "El código de proveedor no puede superar los 36 caracteres";
        }
        if (productForm.length < 1) {
            return "Por favor, añada un articulo como mínimo";
        }
        return null;
    };

    const handleCreateOrder = async (event) => {
        event.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            setLoading(true);
            const result = await createOrder({ ...orderForm, products: productForm });

            if (result.status < 300) {
                if (result.data != null && result.data.status_code >= 300) {
                    showSnackbar(`Error: ${result.data.detail}`, 'error');
                } else {
                    showSnackbar('Pedido creado correctamente', 'success');
                    navigate(`/orders/list`);
                    setError(null);
                }
            } else {
                setError(`Error ${result.status}: ${result.data.detail}`);
            }
        } catch (error) {
            setError(handleApiError(error, 'Error al crear pedido.'));
        } finally {
            setLoading(false);
        }
    };
    const handleNewProductChange = (event) => {
        const { name, value } = event.target;

        let errorMessage = null;

        const regex = /^\d+(\.\d{1,2})?$/;

        if (name === "price" || name === "quantity") {

            if (!regex.test(value)) {
                errorMessage = "El valor debe tener máximo dos decimales y ser mayor que 0.";
            }

            if (name === "price") {
                setErrorPriceMessage(errorMessage);
            } else if (name === "quantity") {
                setErrorQuantityMessage(errorMessage);
            }
        } else if (name === "product_name") {
            if (value.length > 36) {
                errorMessage = "El nombre del producto debe tener como máximo 36 caracteres.";
                setErrorProductNameMessage(errorMessage);
            } else {
                setErrorProductNameMessage(null);  // Limpia el mensaje de error si es válido
            }
        }
        setNewProduct(prevState => ({
            ...prevState,
            [name]: value
        }));
    };
    const handleAddProduct = () => {
        if (newProduct.product_name && newProduct.price && newProduct.quantity && newProduct.unit) {
            setProductForm([...productForm, newProduct]);
            setNewProduct(initialNewProductState);  // Reset fields after adding product
        }
    };

    const handleRemoveProduct = (index) => {
        const newProductForm = productForm.filter((_, i) => i !== index);
        setProductForm(newProductForm);
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Paper elevation={3} style={{ padding: 20, width: '80%' }}>
                <Typography component="h2" variant="h5">
                    Nuevo Pedido
                </Typography>
                <br />
                <form onSubmit={handleCreateOrder}>
                    <Grid container spacing={3} alignItems="center" wrap="nowrap">
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                options={providers}
                                getOptionLabel={(option) => option.code?.toString() + ' - ' + option.name?.toString()}
                                value={selectedProvider}
                                onChange={handleProviderChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Proveedor"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                options={buildings}
                                getOptionLabel={(option) => option.code?.toString() + ' - ' + option.name?.toString()}
                                value={selectedBuilding}
                                onChange={handleBuildingChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Obra"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                name="provider_code"
                                label="Código proporcionado por proveedor"
                                value={orderForm.provider_code}
                                inputProps={{ maxLength: 36 }}
                                onChange={handleInputChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <DatePickerComponent
                                label="Fecha"
                                value={orderForm.order_date}
                                onChange={handleDateChange}
                            />
                        </Grid>
                    </Grid>
                    <br />
                    <Typography variant="h6">Añadir Producto</Typography>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <TextField
                                name="product_name"
                                label="Nombre del Producto"
                                value={newProduct.product_name}
                                onChange={handleNewProductChange}
                                fullWidth
                                inputProps={{ maxLength: 36 }}
                                error={!!errorProductNameMessage}
                                helperText={errorProductNameMessage}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                name="price"
                                label="Precio por Unidad"
                                type="number"
                                value={newProduct.price}
                                onChange={handleNewProductChange}
                                fullWidth
                                error={!!errorPriceMessage}
                                helperText={errorPriceMessage}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                name="quantity"
                                label="Cantidad"
                                type="number"
                                value={newProduct.quantity}
                                onChange={handleNewProductChange}
                                fullWidth
                                error={!!errorQuantityMessage}
                                helperText={errorQuantityMessage}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="unit-label">Unidad</InputLabel>
                                <Select
                                    labelId="unit-label"
                                    name="unit"
                                    value={newProduct.unit}
                                    onChange={handleNewProductChange}
                                    label="Unidad"
                                >
                                    <MenuItem value="uds">UDS</MenuItem>
                                    <MenuItem value="kg">KG</MenuItem>
                                    <MenuItem value="l">L</MenuItem>
                                    <MenuItem value="hora">HORA</MenuItem>
                                    <MenuItem value="m3">M3</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddProduct}
                                disabled={!!errorPriceMessage || !!errorQuantityMessage || !newProduct.product_name || !newProduct.price || !newProduct.quantity || !newProduct.unit}
                            >
                                Añadir Producto
                            </Button>
                        </Grid>
                    </Grid>
                    <br />
                    <TableContainer component={TablePaper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell>Precio por Unidad</TableCell>
                                    <TableCell>Cantidad</TableCell>
                                    <TableCell>Unidad</TableCell>
                                    <TableCell>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {productForm.map((product, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{product.product_name}</TableCell>
                                        <TableCell>{product.price}</TableCell>
                                        <TableCell>{product.quantity}</TableCell>
                                        <TableCell>{product.unit}</TableCell>
                                        <TableCell>
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveProduct(index)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Button type="submit" variant="contained" color="primary" disabled={loading}>
                            {loading ? <CircularProgress size={24} /> : 'Crear Pedido'}
                        </Button>
                    </Box>
                </form>
                {error && (
                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                        {error}
                    </Typography>
                )}
            </Paper>
        </div>
    );
}

export default NewOrder;
